import React, { useState } from 'react';
import { Alert, Button, Form, Input, Modal } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { closeOrderManually, ORDER_MANUAL_CLOSE_ERROR_KEYS } from '../api/orderStatusApi';
import { coverageMarkerLabel, formatRemainderLines } from '../../invoices/invoiceCoverageLabels';
import type { InvoiceCoverage } from '../../../types/invoiceCoverage';
import { useFeedback } from '../../../utils/feedback';

const MAX_REASON_LENGTH = 500;

interface OrderManualCloseButtonProps {
  orderId: string;
  orderNumber: string;
  coverage: InvoiceCoverage;
  /** The ZK is settled by advance invoices (no quantity coverage). */
  advances?: boolean;
  onClosed: () => Promise<unknown> | void;
}

/**
 * „Zamknij zamówienie” (Emil, 2026-10-19): ADMIN closes a ZK whose approved
 * sales invoices cover only part of it (e.g. 99 %). The page shows the button
 * only for ADMIN on an open, partly invoiced ZK; the backend
 * (`POST /orders/:id/close-manually`) owns the rules and the closing effects.
 */
export default function OrderManualCloseButton({ orderId, orderNumber, coverage, advances = false, onClosed }: OrderManualCloseButtonProps) {
  const { t } = useTranslation(['orders', 'invoices', 'common']);
  const feedback = useFeedback();
  const [form] = Form.useForm<{ reason: string }>();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const remainder = advances ? [] : coverage.lines.filter((line) => line.remainingQuantity == null || line.remainingQuantity > 0);

  const submit = async ({ reason }: { reason: string }) => {
    setSubmitting(true);
    try {
      await closeOrderManually(orderId, reason.trim());
      feedback.success(t('orders:manualClose.success', { orderNumber }));
      setOpen(false);
      form.resetFields();
      await onClosed();
    } catch (error: any) {
      const key = ORDER_MANUAL_CLOSE_ERROR_KEYS[error?.response?.data?.code];
      feedback.error(t('orders:manualClose.errors.failed', { orderNumber, reason: key ? t(key) : t('orders:manualClose.errors.unknown') }));
    } finally {
      setSubmitting(false);
    }
  };

  return <>
    <Button icon={<LockOutlined />} onClick={() => setOpen(true)} data-testid="zk-manual-close">
      {t('orders:manualClose.button')}
    </Button>
    <Modal
      title={t('orders:manualClose.title', { orderNumber })}
      open={open}
      onCancel={() => setOpen(false)}
      maskClosable={false}
      okText={t('orders:manualClose.confirm')}
      cancelText={t('common:cancel')}
      okButtonProps={{ loading: submitting, 'data-testid': 'zk-manual-close-confirm' } as any}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={advances ? t('orders:manualClose.advances') : coverageMarkerLabel(coverage, t)}
        description={<>
          {remainder.length > 0 && <div>{t('orders:manualClose.remainder', { lines: formatRemainderLines(remainder) })}</div>}
          <div>{t('orders:manualClose.effect')}</div>
        </>}
      />
      <Form form={form} layout="vertical" onFinish={submit} preserve={false}>
        <Form.Item
          name="reason"
          label={t('orders:manualClose.reason')}
          rules={[{ required: true, whitespace: true, message: t('orders:manualClose.errors.reasonRequired') }]}
        >
          <Input.TextArea rows={3} maxLength={MAX_REASON_LENGTH} placeholder={t('orders:manualClose.reasonPlaceholder')} data-testid="zk-manual-close-reason" />
        </Form.Item>
      </Form>
    </Modal>
  </>;
}
